import {
  Button,
  ColorsV2,
  Flex,
  IconInCircle,
  sizes,
  spaces,
  Text,
  TextInputField,
  useThemeColors,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useResendVerificationEmailMutation } from '@/redux/slices/userApiSlice';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { emailRegex } from '@/constants/email';

type EmailNotVerifiedErrorPageProps = {
  handleLogOut: () => void;
};

type ResendEmailForm = {
  email: string;
};

const getResendEmailSchema = (t: (key: string) => string) =>
  yup.object({
    email: yup
      .string()
      .matches(emailRegex, t('errors.emailNotVerified.validation.invalidEmail'))
      .required(t('errors.emailNotVerified.validation.emailRequired')),
  });

const EmailNotVerifiedErrorPage = ({
  handleLogOut,
}: EmailNotVerifiedErrorPageProps) => {
  const { size_56 } = sizes;
  const { space_m, space_l, space_xl } = spaces;
  const { t } = useTranslation('common');
  const [iconColor, textSecondary] = useThemeColors([
    ColorsV2.PRIMARY_DARKEST,
    ColorsV2.TEXT_LIGHT,
  ]);

  const [showForm, setShowForm] = useState(false);
  const [showEmailSent, setshowEmailSent] = useState<string | undefined>(
    undefined
  );

  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const [resendVerificationEmail, { isLoading }] =
    useResendVerificationEmailMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendEmailForm>({
    resolver: yupResolver(getResendEmailSchema(t)),
  });

  const onSubmit = async (data: ResendEmailForm) => {
    try {
      await resendVerificationEmail({ email: data.email }).unwrap();
      showSuccessToast({
        id: 'verification-email-sent',
        message: t('errors.emailNotVerified.successToast'),
      });
      setShowForm(false);
      setshowEmailSent(data.email);
    } catch (error) {
      showErrorToast({
        id: 'verification-email-error',
        message: t('errors.emailNotVerified.errorToast'),
      });
    }
  };

  return (
    <Flex
      width="100%"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mt="10rem">
      <IconInCircle
        icon="envelope"
        intent="primary"
        color={iconColor}
        size={size_56}
        smoothedBorder={true}
        mb={space_l}
      />
      <Text variant="heading-2" mb={space_m} textAlign="center">
        {t('errors.emailNotVerified.title')}
      </Text>
      <Text mb={space_m} textAlign="center">
        {t('errors.emailNotVerified.message')}
        <br />
        {t('errors.emailNotVerified.instructions')}
      </Text>
      <Text
        mb={space_l}
        color={textSecondary}
        variant="body-2"
        textAlign="center">
        {t('errors.emailNotVerified.spamNote')}
      </Text>

      <Button
        id="button-log-out-error-page"
        buttonSize="large"
        onClick={handleLogOut}>
        {t('errors.emailNotVerified.logOut')}
      </Button>

      <Flex mt={space_xl}>
        {showEmailSent ? (
          <Flex
            flexDirection="column"
            alignItems="center"
            gap={spaces.space_xs}>
            <Text textAlign="center">
              {t('errors.emailNotVerified.confirmationMessage', {
                email: showEmailSent,
              })}
            </Text>
          </Flex>
        ) : !showForm ? (
          <Button
            id="button-resend-verification-email"
            buttonSize="large"
            variant="text"
            onClick={() => setShowForm(true)}
            mb={space_m}>
            {t('errors.emailNotVerified.resendButton')}
          </Button>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex
              flexDirection="row"
              alignItems="center"
              minWidth="50rem"
              shrink={0}
              mb={space_m}
              gap={space_m}>
              <TextInputField
                id="resend-email-input"
                label={t('errors.emailNotVerified.emailLabel')}
                name="email"
                placeholder={t('errors.emailNotVerified.emailPlaceholder')}
                error={!!errors.email?.message}
                control={control}
                mb={space_m}
              />
              <Button
                id="button-submit-resend-email"
                iconAfter="faPaperPlane"
                type="submit"
                variant="outline"
                width="12rem"
                disabled={isLoading}>
                {t('errors.emailNotVerified.submitButton')}
              </Button>
            </Flex>
          </form>
        )}
      </Flex>
    </Flex>
  );
};

export default EmailNotVerifiedErrorPage;
